import { isEqual } from "@tldraw/utils";
import type { TLRecord } from "@tldraw/tlschema";

type Fields = Record<string, unknown>;
const isObject = (value: unknown): value is Fields =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/** Arrays (including rich text and stroke points) are indivisible values. */
export function mergeFields<T extends object>(
  current: T,
  before: object,
  after: object,
  preserveNewer: boolean,
): T {
  const currentFields = current as Fields,
    from = before as Fields,
    to = after as Fields;
  const next = { ...currentFields };
  for (const key of new Set([...Object.keys(from), ...Object.keys(to)])) {
    if (isEqual(from[key], to[key])) continue;
    if (
      isObject(currentFields[key]) &&
      isObject(from[key]) &&
      isObject(to[key])
    ) {
      next[key] = mergeFields(
        currentFields[key],
        from[key],
        to[key],
        preserveNewer,
      );
    } else if (!preserveNewer || isEqual(currentFields[key], from[key])) {
      if (Object.hasOwn(to, key)) next[key] = to[key];
      else delete next[key];
    }
  }
  return next as T;
}

/** Check the fields owned by an action, while retaining independently edited
 * fields. Arrays remain atomic. Structural identity/parent changes conflict. */
function matchesChanges(
  current: Fields,
  before: Fields,
  after: Fields,
): boolean {
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (isEqual(before[key], after[key])) continue;
    if (
      isObject(before[key]) &&
      isObject(after[key]) &&
      isObject(current[key])
    ) {
      if (!matchesChanges(current[key], before[key], after[key])) return false;
    } else if (
      Object.hasOwn(current, key) !== Object.hasOwn(after, key) ||
      !isEqual(current[key], after[key])
    )
      return false;
  }
  return true;
}

export function reverseOwnedChange(
  current: TLRecord | null,
  before: TLRecord | null,
  after: TLRecord | null,
): { before: TLRecord | null; after: TLRecord | null } | undefined {
  // Receipts and wire records are JSON. Live rich text can contain optional
  // `undefined` marks/attributes that disappear in that representation.
  const canonical = (record: TLRecord | null): TLRecord | null =>
    record && JSON.parse(JSON.stringify(record));
  current = canonical(current);
  before = canonical(before);
  after = canonical(after);
  if (!before || !after) {
    return isEqual(current, after)
      ? { before: current, after: before }
      : undefined;
  }
  if (
    !current ||
    current.id !== after.id ||
    current.typeName !== after.typeName
  )
    return undefined;
  if (
    current.typeName === "shape" &&
    after.typeName === "shape" &&
    (current.type !== after.type ||
      current.parentId !== after.parentId ||
      ("assetId" in after.props &&
        (!("assetId" in current.props) ||
          current.props.assetId !== after.props.assetId)))
  )
    return undefined;
  // Attribution is stamped on every agent write; another action changing an
  // independent field must not conflict solely on its newer batch stamp.
  const comparableBefore = {
    ...before,
    meta: { ...before.meta, agent: after.meta.agent },
  };
  if (
    !matchesChanges(
      current as unknown as Fields,
      comparableBefore as unknown as Fields,
      after as unknown as Fields,
    )
  )
    return undefined;
  return { before: current, after: mergeFields(current, after, before, true) };
}
